import type { ServicePageData } from './types';

const windowGraphics: ServicePageData = {
  slug: 'window-graphics',
  eyebrow: 'Signage',
  title: 'Make your glass work harder with',
  titleAccent: 'printed and cut window graphics.',
  intro:
    'From a full-colour printed display to a simple set of opening-hours vinyls, window graphics turn glass that is normally wasted space into another place to advertise, brand or screen your shop or office.',
  heroImages: [
    '/home/window-privacy-film/hagley-etch.jpg',
    '/home/window-privacy-film/black-heart-etch.jpg',
    '/home/window-privacy-film/kemp-etch.jpg',
    '/home/window-privacy-film/melting-pot-etch.jpg',
    '/home/window-privacy-film/petal-bar-etch.jpg',
    '/home/window-privacy-film/salon4-etch.jpg',
    '/home/window-privacy-film/vets-etch.jpg',
    '/home/window-privacy-film/fs-etch.jpg',
    '/home/window-privacy-film/bamboo-etch.jpg',
    '/home/window-privacy-film/scouts-etch.jpg',
    '/home/window-privacy-film/hagley-etch2.jpg',
    '/home/window-privacy-film/melting-pot-etch2.jpg',
    '/home/window-privacy-film/kearns-etch-bts.jpg',
    '/home/window-privacy-film/etch-bts.jpg',
    '/home/signage/glass-manifestation.jpg',
  ],

  useCases: [
    { title: 'Shop fronts', text: 'Full-colour graphics that turn a window into a display, no matter what is behind it.' },
    { title: 'Offices and studios', text: 'Screen a reception or meeting room while keeping natural light in.' },
    { title: 'Opening hours and contact details', text: 'Simple, permanent vinyls that answer the question before anyone knocks.' },
    { title: 'Promotions and seasonal displays', text: 'Easy to remove and replace when the offer changes.' },
    { title: 'Vehicle and van windows', text: 'One-way vision film that reads from outside but does not block the driver\'s view.' },
  ],

  optionsHeading: 'Choose your window graphic',
  optionsIntro: 'All window graphics are cut or printed to your exact glass measurements and applied by hand.',
  options: [
    {
      id: 'printed-vinyl',
      title: 'Full-colour printed vinyl',
      lead: 'Photos, gradients and detailed artwork, printed edge to edge.',
      body: 'Your design is printed in full colour onto self-adhesive vinyl, laminated and applied to the glass. The option for anything beyond flat colour, including photography and complex branding.',
      image: '/home/window-privacy-film/melting-pot-etch.jpg',
      specs: [
        { label: 'Size', value: 'Cut and tiled to fit any window, single panel or a full shop front run.' },
        { label: 'Finish', value: 'Gloss or matt laminate, applied to the inside or outside of the glass.' },
        { label: 'Application', value: 'Fitted by hand, wet or dry application depending on the design.' },
      ],
    },
    {
      id: 'cut-vinyl-lettering',
      title: 'Cut vinyl lettering',
      lead: 'Sharp-edged text and logos cut from coloured vinyl, no background.',
      body: 'A machine cuts your lettering or logo directly from a roll of coloured vinyl, giving a clean edge with no printed background square around it. The usual choice for a shop name or a simple logo.',
      image: '/home/window-privacy-film/kemp-etch.jpg',
      specs: [
        { label: 'Colours', value: 'Any stock vinyl colour, including metallics and frosted finishes.' },
        { label: 'Size', value: 'Any size that fits the glass, single colour or layered for multi-colour designs.' },
        { label: 'Application', value: 'Applied by hand with a transfer tape to keep spacing accurate.' },
      ],
    },
    {
      id: 'one-way-vision',
      title: 'One-way vision (perforated) film',
      lead: 'Full-colour graphics on the outside, visibility kept from the inside.',
      body: 'A perforated film, tiny holes across the surface let people inside see out and keep daylight coming in, while the outside face reads as a solid printed graphic. Common on shop fronts and vehicle rear windows.',
      image: '/home/window-privacy-film/black-heart-etch.jpg',
      specs: [
        { label: 'Perforation', value: 'Roughly 50/50 print to hole ratio, balances visibility with graphic coverage.' },
        { label: 'Size', value: 'Cut to fit any window or vehicle glass shape.' },
        { label: 'Application', value: 'Applied to the outside face of the glass.' },
      ],
    },
    {
      id: 'opening-hours-contact',
      title: 'Opening hours and contact vinyls',
      lead: 'The small, practical details that finish off a shop front.',
      body: 'Cut vinyl sets giving your opening hours, phone number, website and social handles, sized and positioned to sit neatly with your other window graphics or on their own.',
      image: '/home/window-privacy-film/petal-bar-etch.jpg',
      specs: [
        { label: 'Content', value: 'Opening hours, contact details, website and social handles.' },
        { label: 'Colours', value: 'Any stock vinyl colour, matched to your branding.' },
        { label: 'Application', value: 'Applied by hand, quick to update if hours or details change.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Printed on our in-house wide-format printer, then laminated for protection before it goes on the glass.',
    },
    {
      title: 'Cut vinyl',
      text: 'A machine follows your artwork with a sharp blade, cutting lettering and shapes from pre-coloured vinyl rolls.',
    },
    {
      title: 'One-way vision film',
      text: 'A perforated print material used where visibility from inside needs to be kept alongside a printed graphic outside.',
    },
  ],

  gallery: [
    '/home/window-privacy-film/hagley-etch.jpg',
    '/home/window-privacy-film/black-heart-etch.jpg',
    '/home/window-privacy-film/kemp-etch.jpg',
    '/home/window-privacy-film/melting-pot-etch.jpg',
    '/home/window-privacy-film/petal-bar-etch.jpg',
    '/home/window-privacy-film/salon4-etch.jpg',
    '/home/window-privacy-film/vets-etch.jpg',
    '/home/window-privacy-film/fs-etch.jpg',
  ],

  info: [
    {
      title: 'Professional installation',
      text: 'Our insured install team fits window graphics to shop fronts, offices and vehicles across the Midlands.',
      bullets: ['Fully insured', 'Old graphics removed as part of the job on request'],
    },
    {
      title: 'Supply only',
      text: 'Graphics supplied with transfer tape and application instructions for a straightforward self-fit.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Turnaround',
      text: 'Production is usually 5 to 10 working days from approval. Need it sooner? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'Can people still see out through one-way vision film?',
      a: 'Yes, the film is perforated with tiny holes across the surface, so from inside it reads as a mesh you can see through, while the outside face shows the full printed graphic.',
    },
    {
      q: 'Will window graphics damage the glass?',
      a: 'No, they are designed to be removed cleanly. We use a self-adhesive vinyl suited to the surface and the length of time you plan to keep it up.',
    },
    {
      q: 'Can you update just the opening hours later?',
      a: 'Yes, hours and contact vinyls are cut and applied separately from any larger graphic, so they can be swapped without touching the rest of the window.',
    },
  ],

  cta: {
    title: 'Get your free window graphics quote and mockup.',
    text: 'Send us your window sizes and we will recommend the right finish for what you need it to do.',
    image: '/home/signage/glass-manifestation.jpg',
  },
};

export default windowGraphics;
